import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import { CalloutNotification, Checkbox, Input, Select, TextArea, useLocalizeMessage } from 'libs.nucleus';
import { ValidatedTemplateNameInput } from './validated_template_name_input.component';
import { DataTransferTemplateContext } from '../../../contexts/data_transfer_template/data_transfer_template.context';
import { isValidCommaSeparatedStrings, validateDateFormat, validateDateTimeFormat, validateFileNamePattern, validateTimeFormat, } from '../../utils/utils';
export const CreateTransferTemplateBySqlEditorStep2 = () => {
    const translate = useLocalizeMessage();
    const { timezoneOptions, packageTypeOptions, fileExtensionOptions, setCreateTemplateFormData, setIsCreateFormValid, createTemplateFormData, initialData, isEditMode, pivotDataEnabled, setPivotDataEnabled, templateCreationOption, isReadOnly, } = useContext(DataTransferTemplateContext);
    const [isFieldValid, setIsFieldValid] = useState({
        name: true,
        fileNamePattern: true,
        archiveNamePattern: true,
        timeFormat: true,
        dateFormat: true,
        datetimeFormat: true,
        excludedTasks: true,
        excludedSteps: true,
    });
    const [zipOutput, setZipOutput] = useState(false);
    const fieldValidatorFns = {
        fileNamePattern: validateFileNamePattern,
        archiveNamePattern: validateFileNamePattern,
        timeFormat: validateTimeFormat,
        dateFormat: validateDateFormat,
        datetimeFormat: validateDateTimeFormat,
        excludedTasks: isValidCommaSeparatedStrings,
        excludedSteps: isValidCommaSeparatedStrings,
    };
    const [form, setForm] = useState({
        ...createTemplateFormData,
        packageType: packageTypeOptions.find((option) => option.value === createTemplateFormData?.packageType) ??
            packageTypeOptions[0],
        fileExtension: fileExtensionOptions.find((option) => option.value === createTemplateFormData?.fileExtension) ??
            fileExtensionOptions[0],
        timezone: timezoneOptions.find((option) => option.value === createTemplateFormData?.timezone) ?? timezoneOptions[0],
    });
    useEffect(() => {
        // Only reset local form when in edit mode and initialData has meaningful data
        // This prevents resetting the form during normal navigation
        if (isEditMode && initialData && Object.keys(initialData).length > 0) {
            setForm({
                ...initialData,
                packageType: packageTypeOptions.find((option) => option.value === initialData?.packageType) ?? packageTypeOptions[0],
                fileExtension: fileExtensionOptions.find((option) => option.value === initialData?.fileExtension) ?? fileExtensionOptions[0],
                timezone: timezoneOptions.find((option) => option.value === initialData?.timezone) ?? timezoneOptions[0],
            });
        }
    }, [initialData, isEditMode, packageTypeOptions, fileExtensionOptions, timezoneOptions]);
    // Initialize local form with context data when component mounts (for create mode)
    useEffect(() => {
        if (!isEditMode && createTemplateFormData && Object.keys(createTemplateFormData).length > 0) {
            setForm({
                ...createTemplateFormData,
                packageType: packageTypeOptions.find((option) => option.value === createTemplateFormData?.packageType) ??
                    packageTypeOptions[0],
                fileExtension: fileExtensionOptions.find((option) => option.value === createTemplateFormData?.fileExtension) ??
                    fileExtensionOptions[0],
                timezone: timezoneOptions.find((option) => option.value === createTemplateFormData?.timezone) ?? timezoneOptions[0],
            });
        }
    }, [createTemplateFormData, isEditMode, packageTypeOptions, fileExtensionOptions, timezoneOptions]);
    // Sync local zipOutput state with form/context state on mount and when initialData changes
    useEffect(() => {
        setZipOutput(form.zipOutput ?? false);
    }, [form.zipOutput]);
    // If pivot is toggled from enabled to disabled, clear Python transformation
    const [wasPivotEnabled, setWasPivotEnabled] = useState(pivotDataEnabled);
    useEffect(() => {
        // Clear only on transitions from true -> false to avoid wiping existing config on initial mount
        if (wasPivotEnabled && !pivotDataEnabled && Boolean(createTemplateFormData?.py?.operation)) {
            setCreateTemplateFormData({
                ...createTemplateFormData,
                py: undefined,
            });
        }
        setWasPivotEnabled(pivotDataEnabled);
    }, [pivotDataEnabled]);
    const saveDisabled = useMemo(() => {
        return (!form.name ||
            !isFieldValid.name ||
            !form.packageType.value ||
            !form.fileExtension.value ||
            !form.fileNamePattern ||
            !isFieldValid.fileNamePattern ||
            (!form.archiveNamePattern && zipOutput) ||
            (!isFieldValid.archiveNamePattern && zipOutput));
    }, [form, isFieldValid, zipOutput]);
    useEffect(() => {
        setIsCreateFormValid(!saveDisabled);
    }, [saveDisabled, setIsCreateFormValid]);
    const handleFieldChange = (field, value, isSelect = false) => {
        const updatedForm = { ...form, [field]: value };
        setForm(updatedForm);
        // Update form data in context whenever it changes
        setCreateTemplateFormData({
            ...createTemplateFormData,
            [field]: isSelect ? value.value : value,
        });
        if (fieldValidatorFns[field]) {
            setIsFieldValid({ ...isFieldValid, [field]: fieldValidatorFns[field] ? fieldValidatorFns[field](value) : true });
        }
    };
    const handleNameValidationChange = (isValid) => {
        setIsFieldValid((prev) => ({ ...prev, name: isValid }));
    };
    return (
    // Top-align (justify-start) rather than center: when the form is taller than the modal body,
    // centering pushes the heading and Name field above the scroll origin where they can't be reached.
    _jsx("div", { className: 'flex flex-col items-center justify-start size-full', children: _jsxs("div", { className: 'w-[50%] space-y-4 py-8', children: [_jsxs("div", { children: [_jsx("div", { className: 'text-lg font-semibold', children: translate(isReadOnly
                                ? 'View transfer template'
                                : isEditMode
                                    ? 'Edit transfer template'
                                    : 'Create a new transfer template') }), _jsx("div", { className: 'text-sm text-text-secondary', children: translate(isReadOnly ? 'View template information' : 'Edit template information') })] }), _jsxs("div", { className: 'space-y-4', children: [_jsx(ValidatedTemplateNameInput, { value: form.name, onChange: (value) => handleFieldChange('name', value), onValidationChange: handleNameValidationChange, disabled: isReadOnly }), _jsx(TextArea, { id: 'description', label: translate('Description'), value: form.description, onChange: (e) => handleFieldChange('description', e.target.value), rows: 3, placeholder: translate('For data transfer and site archival'), width: 'full', disabled: isReadOnly }), _jsx(Select, { required: true, label: translate('Package type'), options: packageTypeOptions, value: form.packageType, onChange: (value) => handleFieldChange('packageType', value, true), width: 'full', disabled: isReadOnly }), _jsx(Select, { required: true, label: translate('File type'), options: fileExtensionOptions, value: form.fileExtension, onChange: (value) => handleFieldChange('fileExtension', value, true), width: 'full', disabled: isReadOnly }), form?.fileExtension?.value === 'xpt' && (_jsx(CalloutNotification, { type: 'info', role: 'status', title: translate('Note'), subtitle: translate('Column names should not contain spaces, and at most 32 characters') })), _jsx(Input, { required: true, id: 'filenamePattern', label: translate('File name pattern'), description: translate('This can be overridden when configuring a schedule. Available keywords are:') +
                                ' {ENV}, {STUDY}, {PROTOCOL}, Year {YYYY}, Month {MM}, Month short name {MMM}, Month full name {MMMM}, Day {DD}, Hour {hh}, Minute {mm}. ' +
                                translate('Example:') +
                                ' {STUDY}-{YYYY}-{MMM}-{DD}', value: form.fileNamePattern, onChange: (e) => handleFieldChange('fileNamePattern', e.target.value), hasError: !isFieldValid.fileNamePattern, errorMessage: translate('Invalid file name pattern.'), width: 'full', disabled: isReadOnly }), _jsx(Checkbox, { label: translate('Compress .zip files'), checked: zipOutput, onChange: (value) => {
                                setZipOutput(value);
                                handleFieldChange('zipOutput', value);
                            }, disabled: isReadOnly }), zipOutput && (_jsx(Input, { required: true, id: 'archiveNamePattern', label: translate('Compressed file name pattern'), description: translate('This can be overridden when configuring a schedule. Available keywords are:') +
                                ' {ENV}, {STUDY}, {PROTOCOL}, Year {YYYY}, Month {MM}, Month short name {MMM}, Month full name {MMMM}, Day {DD}, Hour {hh}, Minute {mm}. ' +
                                translate('Example:') +
                                ' {STUDY}-{YYYY}-{MMM}-{DD}', value: form.archiveNamePattern, onChange: (e) => handleFieldChange('archiveNamePattern', e.target.value), hasError: !isFieldValid.archiveNamePattern, errorMessage: translate('Invalid compressed file name pattern.'), width: 'full', disabled: isReadOnly })), templateCreationOption === 'sql_editor' && (_jsx(Checkbox, { label: translate('Pivot data'), checked: pivotDataEnabled, onChange: (value) => setPivotDataEnabled(value), disabled: isReadOnly }))] })] }) }));
};
